import { Component, Inject, LOCALE_ID, OnDestroy } from '@angular/core';
import { formatCurrency, formatDate } from '@angular/common';
import { Store, select } from '@ngrx/store';
import { isEqual } from 'lodash';
import { Observable, Subscription, Subject, combineLatest, of } from 'rxjs';
import {
  distinctUntilChanged,
  map,
  filter,
  delay,
  switchMap,
  first
} from 'rxjs/operators';
import {
  GridReadyEvent,
  AgGridEvent,
  ColDef,
  GridOptions,
  GridSizeChangedEvent,
  SortChangedEvent,
  ValueFormatterParams,
  TooltipParams
} from 'ag-grid-community';
import {
  InfiniteScrollingService,
  OrderModel,
  dateComparer,
  numberComparer,
  stringComparer,
  shallowCompare
} from '@ipreo/muni-shared';
import { AccountCellRendererComponent } from '../../../shared/components/renderers/account-cell.component';
import { AccountManager } from '../../../state/data/state';
import { FilteredTranchesWithFilters } from '../../../state/types';
import {
  getSearchByAccountManagerState,
  getTrancheOrdersFilteredWithFiltersState
} from '../../../state/data/reducer';
import { GameDayRootReducerState } from '../../../state/root.state';
import { NegativeMessages } from '../../../state/negative-cases/state';
import { ACCOUNT } from '../../data/gameDay-data.calculation';
import {
  EntityDetailsCardInfo,
  EntityOrder,
  getEntityOrderCardInfo,
  getEntityOrders
} from './entity-details.calculations';
import { OrderNumberComponent } from '../../../shared/components/order-number/order-number.component';
import { GameDayModel } from '../../../models/gameday-model';

@Component({
  selector: 'muni-gameday-entity-order-detail',
  templateUrl: './entity-details.template.html',
  styleUrls: ['./entity-details.scss']
})
export class EntityOrderDetailComponent implements OnDestroy {
  private readonly gridReadySubj = new Subject<GridReadyEvent>();
  private readonly gridResizeSubj = new Subject<AgGridEvent>();
  private customSorting: OrderModel[];

  private readonly columnDefinition: ColDef[] = [
    {
      field: 'orderNumber',
      colId: 'orderNumber',
      headerName: 'Ord #',
      comparator: numberComparer,
      cellRendererFramework: OrderNumberComponent,
      width: 70,
      minWidth: 70,
      maxWidth: 70
    },
    {
      field: 'entity',
      colId: 'manager',
      headerName: 'Manager',
      comparator: stringComparer
    },
    {
      field: 'series',
      colId: 'series',
      headerName: 'Series',
      comparator: stringComparer
    },
    {
      field: 'maturity',
      colId: 'maturity',
      headerName: 'Maturity',
      comparator: dateComparer,
      valueFormatter: ({ value }: ValueFormatterParams) =>
        formatDate(value, 'MM/dd/yyyy', this.locale),
      width: 115,
      minWidth: 115,
      maxWidth: 115
    },
    {
      field: 'type',
      colId: 'type',
      headerName: 'Type',
      comparator: stringComparer
    },
    {
      field: 'priority',
      colId: 'priority',
      headerName: 'Priority',
      comparator: stringComparer
    },
    {
      field: 'amount',
      colId: 'amount',
      headerName: 'Amount ($M)',
      comparator: numberComparer,
      sort: 'desc',
      valueFormatter: ({ value }: ValueFormatterParams) =>
        formatCurrency(value, this.locale, '$', 'USD', '1.0-2')
    }
  ];

  public readonly gridOptions: GridOptions = {
    rowModelType: 'infinite',
    rowHeight: 31,
    maxBlocksInCache: 2,
    cacheBlockSize: 30,
    enableServerSideSorting: true,
    suppressMovableColumns: true
  };

  private readonly entityDetails: Observable<{
    orders: EntityOrder[];
    cardsInfo: EntityDetailsCardInfo;
  }> = this.store.pipe(
    select(getTrancheOrdersFilteredWithFiltersState),
    filter(
      ({
        tranches: {
          filtered: { deal, orders }
        }
      }) => !!(deal && orders)
    ),
    map(
      ({
        tranches,
        filters,
        filters: { searchByAccountManager }
      }: FilteredTranchesWithFilters) => {
        const orders = getEntityOrders({ tranches, filters });

        return {
          orders,
          cardsInfo: getEntityOrderCardInfo(orders, searchByAccountManager)
        };
      }
    ),
    distinctUntilChanged(shallowCompare)
  );

  private readonly searchBy: Observable<AccountManager> = this.store.pipe(
    select(getSearchByAccountManagerState),
    filter((account: AccountManager) => !!account)
  );

  private readonly isSearchByAccount: Observable<boolean> = this.store.pipe(
    select(getSearchByAccountManagerState),
    filter((account) => Boolean(account)),
    map((account: AccountManager) => {
      return account.type === ACCOUNT;
    })
  );

  public readonly entityOrders: Observable<
    EntityOrder[]
  > = this.entityDetails.pipe(
    map(({ orders }: { orders: EntityOrder[] }) => orders)
  );

  public readonly emptyMessage: Observable<string> = this.entityOrders.pipe(
    switchMap((orders: EntityOrder[]) => {
      if (orders.length) {
        return of('');
      }

      return this.searchBy.pipe(
        map(() => NegativeMessages.NoFilteredDealOrders)
      );
    })
  );

  public readonly cardSource: Observable<EntityDetailsCardInfo> = this.entityDetails.pipe(
    map(({ cardsInfo }: { cardsInfo: EntityDetailsCardInfo }) => cardsInfo)
  );

  public readonly entity: Observable<AccountManager> = this.cardSource.pipe(
    filter((info) => !!info),
    map(({ entity, name }: EntityDetailsCardInfo) => ({
      type: entity,
      name
    })),
    distinctUntilChanged()
  );

  public readonly isLinkClickable: Observable<boolean> = this.searchBy.pipe(
    filter((account: AccountManager) => !!account),
    map(
      ({ priority }) => priority !== GameDayModel.AccountPriority.brockerDealer
    )
  );

  private readonly grid$: Observable<
    [GridReadyEvent, EntityOrder[], boolean]
  > = combineLatest(
    this.gridReadySubj.pipe(first()),
    this.entityOrders,
    this.isSearchByAccount
  ).pipe(distinctUntilChanged(isEqual));

  private readonly gridSubscription: Subscription = this.grid$.subscribe(
    ([event, data, isManagerColumnVisible]) => {
      const { api } = event;
      const colDefs = this.getColumnsDefinitions(isManagerColumnVisible);

      api.setColumnDefs([]);
      api.setColumnDefs(colDefs);

      if (this.customSorting) {
        api.setSortModel(this.customSorting);
      }

      const sortMap = colDefs.map(({ field, comparator }) => ({
        colId: field,
        comparator
      }));

      const dataSource = this.infiniteScrollingService.getDataSource(
        data,
        undefined,
        sortMap,
        () => this.gridResizeSubj.next(event)
      );

      api.setDatasource(dataSource);
    }
  );

  private readonly resizeSubscription: Subscription = this.gridResizeSubj
    .pipe(delay(0))
    .subscribe(({ api }) => api.sizeColumnsToFit());

  constructor(
    private store: Store<GameDayRootReducerState>,
    private infiniteScrollingService: InfiniteScrollingService,
    @Inject(LOCALE_ID) private locale: string
  ) {}

  public onGridReady(event: GridReadyEvent): void {
    this.gridReadySubj.next(event);
  }

  public onGridSizeChanged(event: GridSizeChangedEvent): void {
    this.gridResizeSubj.next(event);
  }

  public preserveCustomSorting({ api }: SortChangedEvent): void {
    this.customSorting = api.getSortModel();
  }

  public ngOnDestroy(): void {
    this.gridSubscription.unsubscribe();
    this.resizeSubscription.unsubscribe();
  }

  private getColumnsDefinitions(isManagerColumnVisible: boolean) {
    const colDefs = this.columnDefinition
      .filter(({ field }: ColDef) => field !== 'entity')
      .map((colDef: ColDef) => ({
        ...colDef
      }));
    const defaultEntityColDef = {
      ...this.columnDefinition.find(({ field }: ColDef) => field === 'entity')
    };

    colDefs.splice(
      isManagerColumnVisible ? 1 : this.columnDefinition.length - 2,
      0,
      isManagerColumnVisible
        ? defaultEntityColDef
        : {
            ...defaultEntityColDef,
            headerName: 'Account',
            colId: 'account',
            cellRendererFramework: AccountCellRendererComponent
          }
    );

    return colDefs.map((colDef: ColDef) => ({
      ...colDef,
      suppressFilter: true,
      suppressMenu: true,
      tooltip: ({ value, valueFormatted }: TooltipParams) =>
        valueFormatted || value
    }));
  }
}
